<!--
	Copyright (c) 2021-present, Bäumer, Afonso, Meissner GmbH
	All rights reserved.
-->
<script lang="ts" setup>
import { computed, toRefs, ref, watchEffect } from 'vue';
import { useClamp, useElementSize, useMouseInElement, useMousePressed } from '@vueuse/core';

const props = withDefaults(defineProps<{ initialHandlePosition: number }>(), {
    initialHandlePosition: 50,
});
const { initialHandlePosition } = toRefs(props);

const rootElement = ref<HTMLElement>();
const { width } = useElementSize(rootElement);
const { elementX } = useMouseInElement(rootElement);
const { pressed } = useMousePressed({ target: rootElement });

const handlePosition = useClamp(initialHandlePosition.value, 0, 100);
const handleX = computed(() => `${(handlePosition.value / 100) * width.value}px`);

watchEffect(() => {
    if (!pressed.value) return;
    handlePosition.value = (elementX.value / width.value) * 100;
});
</script>

<template>
    <div ref="rootElement" class="abslider">
        <div class="abslider__container-a">
            <!-- deprecated -->
            <slot name="imageA" />
            <slot name="imagea" />
        </div>

        <div class="abslider__container-b">
            <!-- deprecated -->
            <slot name="imageB" />
            <slot name="imageb" />
        </div>

        <div class="abslider__handle-container">
            <div class="abslider__drag-handle"></div>
        </div>
    </div>
</template>

<style lang="scss">
@use "@bam/scss" as *;

.abslider {
    @include v(
        (
            scanline-color: hsla(v(color-black-hsl), 0.1),
            handle-border: 3px solid v(color-black),
            handle-border-radius: 4px,
            handle-background-color: v(color-black),
            handle-width: v(size-20),
            handle-height: v(size-42),
        )
    );

    position: relative;
    user-select: none;
    overflow: hidden;

    &__container-a {
        position: relative;
        z-index: 1;
        pointer-events: none;
    }

    &__container-b {
        position: absolute;
        z-index: 2;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        clip-path: inset(0 0 0 v-bind(handleX));
    }

    img {
        width: 100%;
        height: auto;
    }

    &__handle-container {
        position: absolute;
        top: 0;
        left: v-bind(handleX);
        height: 100%;
        z-index: 3;
        display: flex;
        align-items: center;
        justify-content: center;
        transform: translateX(-50%);
        cursor: pointer; /* fallback if grab cursor is unsupported */
        cursor: grab;

        &:before,
        &:after {
            content: '';
            position: absolute;
            width: 3px;
        }

        &:before {
            top: 0;
            bottom: 50%;
            background-image: linear-gradient(to bottom, v(scanline-color) 100%, transparent 0%);
        }

        &:after {
            top: 50%;
            bottom: 0;
            background-image: linear-gradient(to top, v(scanline-color) 100%, transparent 0%);
        }
    }

    &__drag-handle {
        width: v(handle-width);
        height: v(handle-height);
        background-color: v(handle-background-color);
        border-radius: v(handle-border-radius);
        border: v(handle-border);
        position: relative;
        cursor: pointer; /* fallback if grab cursor is unsupported */
        cursor: grab;

        &:before,
        &:after {
            content: '';
            width: v(size-2);
            height: v(size-10);
            display: inline-block;
            background-color: v(color-white);
            position: absolute;
            top: 50%;
            transform: translateY(-50%);
        }

        &:before {
            left: 50%;
            transform: translateY(-50%) translateX(calc(v(size-4) * -1));
        }

        &:after {
            right: 50%;
            transform: translateY(-50%) translateX(v(size-4));
        }
    }
}
</style>
